import { DEFAULT_TEMPLATES } from "../../../data/defaultTemplates";
import type { StrategyFamily } from "../../../types/Strategy";
import './Sidebar.css';
import React, { useMemo, useState } from 'react';

type SavedStrategy = {
  name: string;
  [key: string]: unknown;
};

type PaletteItem = {
  id: string;
  label: string;
  color: string;
  strategyGrammars: StrategyFamily[];
  isTopLevel?: boolean;
};

const BUILDER_ITEMS: PaletteItem[] = [
  { id: 'Single', label: 'Data', color: 'var(--node-data)', strategyGrammars: ['base', 'quantarc', 'meepmeep'] },
  { id: 'Composite', label: 'Combine', color: 'var(--node-combine)', strategyGrammars: ['base', 'quantarc'] },
  { id: 'Formatter', label: 'Transform', color: 'var(--node-transform)', strategyGrammars: ['base', 'quantarc', 'meepmeep'] },
  { id: 'PnL', label: 'PnL', color: 'var(--accent-success)', isTopLevel: true, strategyGrammars: ['base', 'quantarc'] },
  { id: 'Variance', label: 'Variance', color: 'var(--accent-warning)', isTopLevel: true, strategyGrammars: ['base'] },
  { id: 'MeepMeep', label: '⚡ MeepMeep', color: 'var(--meep-cyan)', isTopLevel: true, strategyGrammars: ['meepmeep'] },
];

function getPaletteSections(strategyFamily: StrategyFamily) {
  return {
    buildComponents: BUILDER_ITEMS.filter((item) => !item.isTopLevel && item.strategyGrammars.includes(strategyFamily)),
    rootComponents: BUILDER_ITEMS.filter((item) => !!item.isTopLevel && item.strategyGrammars.includes(strategyFamily)),
  };
}

function dragNodeType(e: React.DragEvent, nodeType: string) {
  e.stopPropagation();
  e.dataTransfer.setData('application/reactflow', nodeType);
}

function dragSavedPayload(e: React.DragEvent, payload: unknown) {
  e.stopPropagation();
  e.dataTransfer.setData('application/moneymaker-saved-strategy', JSON.stringify(payload));
}

function PaletteCard({ item, animationDelay }: { item: PaletteItem; animationDelay?: string }) {
  return (
    <div
      draggable
      onDragStart={(e) => dragNodeType(e, item.id)}
      className="sidebar-item animate-slide-in"
      style={{
        color: item.color,
        animationDelay,
        border: item.id === 'MeepMeep' ? '1px solid var(--meep-cyan)' : undefined,
        background: item.id === 'MeepMeep' ? 'rgba(0, 255, 255, 0.05)' : undefined,
      } as React.CSSProperties}
    >
      <div className="dot-indicator" style={{ backgroundColor: item.color }}></div>
      {item.label}
    </div>
  );
}

export function BuildPalette({ strategyFamily }: { strategyFamily: StrategyFamily }) {
  const { buildComponents, rootComponents } = useMemo(() => getPaletteSections(strategyFamily), [strategyFamily]);

  return (
    <div className="build-palette-shell">
      <div>
        <h3 className="sidebar-section-header">Build Components</h3>
        <div className="build-palette-list">
          {buildComponents.map((item, idx) => (
            <PaletteCard key={item.id} item={item} animationDelay={`${idx * 0.05}s`} />
          ))}
        </div>
      </div>

      <div>
        <h3 className="sidebar-section-header">Strategy Roots</h3>
        <div className="build-palette-list">
          {rootComponents.map((item, idx) => (
            <PaletteCard
              key={item.id}
              item={item}
              animationDelay={`${(buildComponents.length + idx) * 0.05}s`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function StrategyLibraryFlyout({
  savedStrategies,
  onDeleteStrategy,
}: {
  savedStrategies: SavedStrategy[];
  onDeleteStrategy: (name: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const handleDelete = (name: string) => {
    const confirm = window.confirm(`Delete saved strategy "${name}"?`);
    if (!confirm) return;
    onDeleteStrategy(name);
  };

  return (
    <div className={`strategy-library-fab ${isOpen ? 'open' : ''}`}>
      {isOpen && (
        <div className="strategy-library-panel">
          <div className="strategy-library-header">
            <div>
              <div className="strategy-library-title">Library</div>
              <div className="strategy-library-subtitle">Templates and saved strategy drops</div>
            </div>
            <button
              type="button"
              className="strategy-library-close"
              onClick={() => setIsOpen(false)}
              title="Close library"
            >
              ✕
            </button>
          </div>

          <div className="strategy-library-section">
            <h3 className="sidebar-section-header">Templates</h3>
            <div className="strategy-library-scroll">
              {DEFAULT_TEMPLATES.map((template, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={(e) => dragSavedPayload(e, template)}
                  className="template-item animate-slide-in"
                  style={{ animationDelay: `${idx * 0.04}s` } as React.CSSProperties}
                >
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                    <span className="icon">📋</span> {template.name}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="strategy-library-section">
            <h3 className="sidebar-section-header">My Strategies</h3>
            <div className="strategy-library-scroll">
              {savedStrategies.length === 0 && (
                <span className="strategy-library-empty">No attributes saved yet.</span>
              )}

              {savedStrategies.map((strategy, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={(e) => dragSavedPayload(e, strategy)}
                  className="template-item animate-slide-in"
                  style={{ animationDelay: `${(DEFAULT_TEMPLATES.length + idx) * 0.04}s` } as React.CSSProperties}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span className="icon">🧠</span> {strategy.name}
                  </span>
                  <button
                    className="delete-btn"
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(strategy.name);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className="strategy-library-trigger"
        onClick={() => setIsOpen((value) => !value)}
        title={isOpen ? 'Hide templates and saved strategies' : 'Show templates and saved strategies'}
      >
        <span className="strategy-library-trigger-icon">{isOpen ? '×' : '📚'}</span>
        <span>{isOpen ? 'Hide Library' : 'Library'}</span>
      </button>
    </div>
  );
}
